"use client"

import { useEffect, useState } from "react"
import API, { URL_PATH } from "../../services/API"
import Header from "../../components/Header"
import {
  Box,
  Switch,
  IconButton,
  TableContainer,
  Paper,
  FormControl,
  FormLabel,
  Typography,
  RadioGroup,
  Checkbox,
  TextField,
  Grid,
  Button,
  Modal,
  FormControlLabel,
  Radio
} from "@mui/material"
import Toast from "../../components/Toast"
import DeleteIcon from "@mui/icons-material/Delete"
import VisibilityIcon from "@mui/icons-material/Visibility"
import { Upload } from "@mui/icons-material"
import {CustomDatePicker} from "../../components/CustomDatePicker"

const ColorScheme = {
  buttonColor: "#FFC857",
  bgColor: "#02111B",
  componentColor: "#07070A",
  accentColor: "#BB0A21",
  textColor: "#FFFFFF",
}

const BannersPage = () => {

  

  const [banners, setBanners] = useState([])
  const [previewImage, setPreviewImage] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  })
  const [selectAll, setSelectAll] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])
  const [bannerLevel, setBannerLevel] = useState("user") // Default to 'user'

  // Add banner form states
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [selectedImage, setSelectedImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [formError, setFormError] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
    maxWidth: "90vw",
    bgcolor: ColorScheme.bgColor,
    border: `2px solid ${ColorScheme.buttonColor}`,
    boxShadow: 24,
    p: 4,
    borderRadius: "8px",
    maxHeight: "90vh",
    overflow: "auto",
  }

  const handleCloseToast = (event, reason) => {
    if (reason === "clickaway") return
    setToast({ ...toast, open: false })
  }

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity })
  }

  const fetchBanners = async () => {
    const { status, message, data } = await API(this, "get", URL_PATH.listBanners)

    if (status === "success") {
      setBanners(data.data || data)
    } else {
      showToast(message || "Failed to load banners", "error")
    }
  }

  const handleStatusChange = async (bannerId, newStatus) => {
    try {
      const { status, message } = await API(this, "post", URL_PATH.changeStatusBanner(bannerId), {
        status: newStatus,
      })

      if (status === "success") {
        setBanners(banners.map((banner) => (banner.id === bannerId ? { ...banner, status: newStatus } : banner)))
        showToast("Status updated successfully", "success")
      } else {
        showToast(message || "Failed to update status", "error")
      }
    } catch (error) {
      showToast("An error occurred", "error")
    }
  }

  const handleDelete = async (bannerId) => {
    try {
      const { status, message } = await API(this, "post", URL_PATH.deleteBanner(bannerId))

      if (status === "success") {
        setBanners(banners.filter((banner) => banner.id !== bannerId))
        setSelectedRows(selectedRows.filter((id) => id !== bannerId))
        showToast("Banner deleted successfully", "success")
      } else {
        showToast(message || "Failed to delete banner", "error")
      }
    } catch (error) {
      showToast("An error occurred", "error")
    }
  }

  const handleSelectAll = (e) => {
    const checked = e.target.checked
    setSelectAll(checked)
    setSelectedRows(checked ? banners.map((banner) => banner.id) : [])
  }

  const handleRowSelect = (bannerId, checked) => {
    if (checked) {
      setSelectedRows([...selectedRows, bannerId])
    } else {
      setSelectedRows(selectedRows.filter((id) => id !== bannerId))
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const getBannerStatus = (banner) => {
    const today = new Date()
    const startDate = new Date(banner.start_date)
    const endDate = new Date(banner.end_date)

    if (today < startDate) {
      return { text: "Scheduled", color: "#FFC857" }
    } else if (today > endDate) {
      return { text: "Expired", color: "#BB0A21" }
    } else if (banner.status) {
      return { text: "Active", color: "#00A651" }
    } else {
      return { text: "Inactive", color: "#999" }
    }
  }

 
  

  // Image upload handlers
  const isNotValidFileType = (type) => {
    const validTypes = ["image/png", "image/jpg", "image/jpeg"]
    return !validTypes.includes(type)
  }

  const handleImageSelect = (e) => {
    const file = e.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = () => {
      if (isNotValidFileType(file.type)) {
        setFormError("File type is not supported. Please use PNG, JPG, or JPEG.")
        return
      }

      if (file.size >= 1048576) {
        setFormError("File size is too large. Please upload less than 1 MB.")
        return
      }

      const img = new Image()
      img.onload = () => {
        const aspectRatio = img.width / img.height
        const expectedRatio = 16 / 7
        const tolerance = 0.02

        if (Math.abs(aspectRatio - expectedRatio) > tolerance) {
          setFormError("Image must have a 16:7 aspect ratio (e.g., 1600x700, 3200x1400, etc.)")
          return
        }

        setFormError("")
        setSelectedImage(file)
        setImagePreview(reader.result)
      }
      img.src = reader.result
    }
    reader.readAsDataURL(file)
  }

  const validateForm = () => {
    if (!startDate || !endDate) {
      setFormError("Please select both start and end dates")
      return false
    }

    if (!selectedImage) {
      setFormError("Please select an image")
      return false
    }

    const start = new Date(startDate)
    const end = new Date(endDate)
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    if (start < today) {
      setFormError("Start date cannot be in the past")
      return false
    }

    if (end < start) {
      setFormError("End date must be after start date")
      return false
    }

    setFormError("")
    return true
  }
  

  const handleSubmit = async () => {
    if (!validateForm()) return

    setIsSubmitting(true)
    try {
      const formdata = new FormData()
      formdata.append("image", selectedImage)
      formdata.append("start_date", startDate)
      formdata.append("end_date", endDate)
            formdata.append("level", bannerLevel) // Add the level to the form data


      const { status, message, data } = await API(this, "post", URL_PATH.addNewBanner, formdata, {
        "Content-Type": "multipart/form-data",
      })

      if (status === "success") {
        setBanners([...banners, data])
        handleCloseModal()
        showToast("Banner added successfully", "success")
      } else {
        setFormError(message || "Failed to upload banner")
      }
    } catch (error) {
      setFormError("An error occurred while uploading")
    } finally {
      setIsSubmitting(false)
    }
  }




  const handleCloseModal = () => {
    setModalOpen(false)
    setStartDate("")
    setEndDate("")
    setSelectedImage(null)
    setImagePreview(null)
    setFormError("")
    setIsSubmitting(false)
  }

  const clearImage = () => {
    setSelectedImage(null)
    setImagePreview(null)
    setFormError("")
  }

  useEffect(() => {
    
    fetchBanners()
  }, [])

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: ColorScheme.bgColor,
        minHeight: "100vh",
        overflow: "hidden",
      }}
    >
      <Header title="Banners" button="Add Banner" onclick={() => setModalOpen(true)} />

      {banners.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
            flexDirection: "column",
            textAlign: "center",
            color: ColorScheme.textColor,
          }}
        >
          <Typography variant="h5" sx={{ color: ColorScheme.buttonColor, mb: 1 }}>
            No Banners Available
          </Typography>
          <Typography variant="body1" sx={{ color: "#999" }}>
            There are no banners to display. Banners will appear here once they are added.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer
            component={Paper}
            sx={{
              backgroundColor: "#02111B",
              backgroundImage: "none",
              border: "none",
              boxShadow: "none",
              maxHeight: "calc(100vh - 200px)",
              overflow: "hidden",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr
                  style={{
                    backgroundColor: ColorScheme.bgColor,
                    borderBottom: `1px solid ${ColorScheme.buttonColor}`,
                  }}
                >
                  <th
                    style={{
                      padding: "1rem",
                      textAlign: "left",
                      color: ColorScheme.textColor,
                      borderBottom: `1px solid ${ColorScheme.buttonColor}`,
                    }}
                  >
                    <Checkbox
                      checked={selectAll}
                      onChange={handleSelectAll}
                      sx={{
                        color: ColorScheme.buttonColor,
                        "&.Mui-checked": { color: ColorScheme.buttonColor },
                      }}
                    />
                  </th>
                  <th
                    style={{
                      padding: "1rem",
                      textAlign: "left",
                      color: ColorScheme.textColor,
                      borderBottom: `1px solid ${ColorScheme.buttonColor}`,
                    }}
                  >
                    Banner
                  </th>
                  <th
                    style={{
                      padding: "1rem",
                      textAlign: "left",
                      color: ColorScheme.textColor,
                      borderBottom: `1px solid ${ColorScheme.buttonColor}`,
                    }}
                  >
                    Start Date
                  </th>
                  <th
                    style={{
                      padding: "1rem",
                      textAlign: "left",
                      color: ColorScheme.textColor,
                      borderBottom: `1px solid ${ColorScheme.buttonColor}`,
                    }}
                  >
                    End Date
                  </th>
                  <th
                    style={{
                      padding: "1rem",
                      textAlign: "left",
                      color: ColorScheme.textColor,
                      borderBottom: `1px solid ${ColorScheme.buttonColor}`,
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      padding: "1rem",
                      textAlign: "center",
                      color: ColorScheme.textColor,
                      borderBottom: `1px solid ${ColorScheme.buttonColor}`,
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {banners.map((banner) => {
                  const statusInfo = getBannerStatus(banner)
                  return (
                    <tr
                      key={banner.id}
                      style={{
                        backgroundColor: banners.indexOf(banner) % 2 === 0 ? "transparent" : "#041A29",
                        borderBottom: "none",
                      }}
                    >
                      <td style={{ padding: "1rem", verticalAlign: "middle" }}>
                        <Checkbox
                          checked={selectedRows.includes(banner.id)}
                          onChange={(e) => handleRowSelect(banner.id, e.target.checked)}
                          sx={{
                            color: ColorScheme.buttonColor,
                            "&.Mui-checked": { color: ColorScheme.buttonColor },
                          }}
                        />
                      </td>
                      <td style={{ padding: "1rem", verticalAlign: "middle" }}>
                        <Box sx={{ width: 150, height: 80 }}>
                          <img
                            src={banner.image || "/placeholder.svg"}
                            alt={`Banner ${banner.id}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              borderRadius: "4px",
                            }}
                          />
                        </Box>
                      </td>
                      <td
                        style={{
                          padding: "1rem",
                          verticalAlign: "middle",
                          color: ColorScheme.textColor,
                        }}
                      >
                        {formatDate(banner.start_date)}
                      </td>
                      <td
                        style={{
                          padding: "1rem",
                          verticalAlign: "middle",
                          color: ColorScheme.textColor,
                        }}
                      >
                        {formatDate(banner.end_date)}
                      </td>
                      <td style={{ padding: "1rem", verticalAlign: "middle" }}>
                        <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column", gap: 1 }}>
                          <Switch
                            checked={banner.status}
                            onChange={(e) => handleStatusChange(banner.id, e.target.checked)}
                            disabled={new Date() > new Date(banner.end_date)}
                          />
                          {/* <Typography
                            sx={{
                              color: statusInfo.color,
                              fontSize: "0.75rem",
                              fontWeight: "bold",
                            }}
                          >
                            {statusInfo.text}
                          </Typography> */}
                        </Box>
                      </td>
                      <td
                        style={{
                          padding: "1rem",
                          textAlign: "right",
                          verticalAlign: "middle",
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                          <IconButton
                            onClick={() => setPreviewImage(banner.image)}
                            aria-label="view"
                            size="small"
                            sx={{
                              backgroundColor: "#00A651",
                              color: "white",
                              borderRadius: 1,
                              padding: "4px 8px",
                              fontSize: "0.85rem",
                              "&:hover": { backgroundColor: "#00A651" },
                            }}
                          >
                            <VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />
                            <span>VIEW</span>
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(banner.id)}
                            aria-label="delete"
                            size="small"
                            sx={{
                              backgroundColor: ColorScheme.accentColor,
                              color: "white",
                              borderRadius: 1,
                              padding: "4px 8px",
                              fontSize: "0.85rem",
                              "&:hover": { backgroundColor: "#a3091d" },
                            }}
                          >
                            <DeleteIcon fontSize="small" sx={{ mr: 0.5 }} />
                            <span>DELETE</span>
                          </IconButton>
                        </Box>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </TableContainer>
        </Box>
      )}

      {/* Add Banner Modal */}
      <Modal open={modalOpen} onClose={handleCloseModal}>
        <Box sx={modalStyle}>
          <Typography variant="h5" sx={{ color: ColorScheme.textColor, mb: 3}}>
            Add New Banner
          </Typography>
<FormControl component="fieldset" sx={{ mb: 3 }}>
        <FormLabel component="legend" sx={{ color: "white" }}>
          Banner Level
        </FormLabel>
        <RadioGroup
          row
          aria-label="level"
          name="level"
          value={bannerLevel}
          onChange={(e) => setBannerLevel(e.target.value)}
        >
          <FormControlLabel
            value="user"
            control={
              <Radio
                sx={{
                  color: ColorScheme.buttonColor,
                  '&.Mui-checked': {
                    color: ColorScheme.buttonColor,
                  },
                }}
              />
            }
            label="User"
            sx={{ color: ColorScheme.textColor }}
          />
          <FormControlLabel
            value="hotel"
            control={
              <Radio
                sx={{
                  color: ColorScheme.buttonColor,
                  '&.Mui-checked': {
                    color: ColorScheme.buttonColor,
                  },
                }}
              />
            }
            label="Global"
            sx={{ color: ColorScheme.textColor }}
          />
        </RadioGroup>
      </FormControl>
          {/* Date Selection */}
         {/* Date Selection */}
<Grid container spacing={2} sx={{ mb: 3 }}>
  <Grid item xs={12} sm={6}>
    <CustomDatePicker
      label="Start Date"
      placeholder="dd-mm-yyyy"
      fullWidth
      value={startDate}
      onChange={(e) => setStartDate(e.target.value)}
      InputLabelProps={{
        shrink: true,
      }}
    />
  </Grid>
  <Grid item xs={12} sm={6}>
    <CustomDatePicker
      label="End Date"
      placeholder="dd-mm-yyyy"
      fullWidth
      value={endDate}
      onChange={(e) => setEndDate(e.target.value)}
      InputLabelProps={{
        shrink: true,
      }}
    />
  </Grid>
</Grid>

          {/* Date Summary */}
         

          {/* Image Upload Section */}
          <Box sx={{ mb: 3 }}>
            <input type="file" onChange={handleImageSelect} id="banner-upload" hidden accept="image/*" />

            {!imagePreview ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  padding: "40px 20px",
                  border: "2px dashed #555",
                  borderRadius: "8px",
                  backgroundColor: "#02111B",
                  cursor: "pointer",
                  color: ColorScheme.textColor,
                  textAlign: "center",
                  
                }}
                onClick={() => document.getElementById("banner-upload").click()}
              >
                <Box
                  sx={{
                    backgroundColor: "transparent",
                    border: `1px dashed ${ColorScheme.textColor}`,
                    borderRadius: "4px",
                    width: "50px",
                    height: "50px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <Upload sx={{ color: ColorScheme.textColor }} />
                </Box>
                <Typography sx={{ fontSize: "16px", marginBottom: "8px" }}>Upload Banner Image</Typography>
                <Typography sx={{ fontSize: "14px", color: "#aaa" }}>PNG, JPG (16:7 aspect ratio, max 1MB)</Typography>
              </Box>
            ) : (
              <Box sx={{ textAlign: "center" }}>
                <img
                  src={imagePreview || "/placeholder.svg"}
                  style={{
                    width: "100%",
                    maxHeight: "200px",
                    objectFit: "contain",
                    borderRadius: "4px",
                    border: `1px solid ${ColorScheme.buttonColor}`,
                  }}
                  alt="Preview"
                />
                <Button
                  onClick={clearImage}
                  sx={{
                    mt: 2,
                    backgroundColor: "#02111B",
                    border: `1px solid ${ColorScheme.buttonColor}`,
                    color: "white",
                    "&:hover": { backgroundColor: "#02111B" },
                  }}
                >
                  Change Image
                </Button>
              </Box>
            )}
          </Box>

          {/* Error Display */}
          {formError && (
            <Typography sx={{ color: ColorScheme.accentColor, mb: 2, textAlign: "center" }}>{formError}</Typography>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
            <Button
              onClick={handleCloseModal}
              sx={{
                backgroundColor: ColorScheme.accentColor,
                color: "white",
                fontWeight: "700",
                flex: 1,
                "&:hover": { backgroundColor: ColorScheme.accentColor },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              // disabled={isSubmitting || !startDate || !endDate || !selectedImage}
              sx={{
                backgroundColor: ColorScheme.buttonColor,
                color: "black",
                fontWeight: "700",
                flex: 1,
                "&:hover": { backgroundColor: ColorScheme.buttonColor },
                // "&:disabled": { backgroundColor: "#666", color: "#999" },
              }}
            >
              {isSubmitting ? "Adding..." : "Add Banner"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Preview Modal */}
      <Modal open={previewImage !== null} onClose={() => setPreviewImage(null)}>
        <Box sx={modalStyle}>
          <Typography variant="h5" sx={{ color: ColorScheme.textColor, mb: 2, textAlign: "center" }}>
            Banner Preview
          </Typography>
          <img
            src={previewImage || "/placeholder.svg"}
            alt="preview"
            style={{
              width: "100%",
              maxHeight: "70vh",
              objectFit: "contain",
              borderRadius: "4px",
            }}
          />
        </Box>
      </Modal>

      <Toast open={toast.open} message={toast.message} severity={toast.severity} handleClose={handleCloseToast} />
    </Box>
  )
}

export default BannersPage
